import React from "react";
import {Citation} from "./inline/Citation";

// Clean up [Ln] style citations:
// - [0 L12][0 L13] → [0] (deduplicate after stripping)
// - [0 L12] → [0] (keep the numeric ref, strip the L-part)
// - [L12] → "" (standalone L-citations are removed entirely)
export const stripLCitations = (text: string): string =>
    text
        .replace(/\[(\d+)\s+L\d+\]/g, "[$1]")
        .replace(/\s*\[L\d+\]/g, "")
        .replace(/(\[\d+\])(\s*\1)+/g, "$1");

// Escape [n] citation brackets so markdown doesn't parse them as link references.
// \[1\] is rendered by markdown as literal [1] text, which processReferences can handle.
export const escapeCitationBrackets = (text: string): string =>
    text.replace(/\[(\d+)\]/g, "\\[$1\\]");

// Fix broken nested markdown links: [any label]([Title](url)) → [Title](url)
// LLMs sometimes wrap a valid markdown link inside another link's URL.
export const fixNestedMarkdownLinks = (text: string): string =>
    text.replace(
        /\[[^\]]+\]\((\[[^\]]+\]\([^)]+\))\)/g,
        "$1",
    );

// Process standalone references that aren't attached to a word
const processStandaloneRefs = (
    text: string,
    keyPrefix: string
): React.ReactNode[] => {
    const cleaned = stripLCitations(text);
    const parts = cleaned.split(/(\[\d+\])/g);
    return parts
        .filter((p) => p !== "")
        .map((part, index) => {
            if (part.match(/^\[\d+\]$/)) {
                return (
                    <Citation key={`${keyPrefix}-${index}`} reference={part} />
                );
            }
            return part;
        });
};

// Process string content to group words with their following references
// This prevents line breaks between a word and its citations (e.g., "word[1][2]" or "word [1][2]")
const processStringContent = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    const cleaned = stripLCitations(text);
    let lastIndex = 0;

    // Pattern matches: word followed by (optional space +) one or more references
    // - ([^\s\[\]]+) captures the word (no brackets or whitespace)
    // - (\s?) captures optional space between word and first ref
    // - ((?:\[\d+\])(?:\s*\[\d+\])*) captures first ref + any following refs (with optional spaces between)
    const wordWithRefsPattern =
        /([^\s\[\]]+)(\s?)((?:\[\d+\])(?:\s*\[\d+\])*)/g;
    let match;

    while ((match = wordWithRefsPattern.exec(cleaned)) !== null) {
        // Add text before this match, processing any standalone references
        if (match.index > lastIndex) {
            result.push(
                ...processStandaloneRefs(
                    cleaned.slice(lastIndex, match.index),
                    `pre-${match.index}`
                )
            );
        }

        const word = match[1];
        const space = match[2];
        const refs = match[3];

        // Split refs into individual citations
        const refMatches = refs.match(/\[\d+\]/g) || [];

        // Wrap word + citations in nowrap span to prevent line breaks
        result.push(
            <span key={match.index} style={{whiteSpace: "nowrap"}}>
                {word}
                {space}
                {refMatches.map((ref, i) => (
                    <Citation key={i} reference={ref} />
                ))}
            </span>
        );

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match, processing any standalone references
    if (lastIndex < cleaned.length) {
        result.push(
            ...processStandaloneRefs(
                cleaned.slice(lastIndex),
                `post-${lastIndex}`
            )
        );
    }

    // If no matches found, process entire text for standalone refs
    if (result.length === 0) {
        return processStandaloneRefs(cleaned, "standalone");
    }

    return result;
};

// This helper function processes inline references (like [1], [2], etc.)
export const processReferences = (
    content: React.ReactNode
): React.ReactNode => {
    if (typeof content !== "string") {
        if (React.isValidElement(content)) {
            return React.cloneElement(content, {
                children: processReferences(content.props.children),
            });
        }
        if (Array.isArray(content)) {
            return content.map((child, index) => (
                <React.Fragment key={index}>
                    {processReferences(child)}
                </React.Fragment>
            ));
        }
        return content;
    }

    return processStringContent(content);
};
